/**
 * Report Service
 *
 * API client for radiology report operations
 */

import { get, post, api } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  Report,
  ReportFilters,
  ReportSearchResult,
  CreateReportData,
  UpdateReportData,
  AmendReportData,
} from '@/domain/report'
import type { ReportStatus } from '@/config/constants'

// =============================================================================
// Types
// =============================================================================

interface ReportResponse {
  report: Report
  message?: string
}

interface ReportsResponse {
  reports: Report[]
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Search/list reports with filters
 */
export async function getReports(filters?: ReportFilters): Promise<ReportSearchResult> {
  const params = new URLSearchParams()

  if (filters?.studyId) params.append('studyId', filters.studyId)
  if (filters?.createdBy) params.append('createdBy', filters.createdBy)
  if (filters?.signedBy) params.append('signedBy', filters.signedBy)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
  if (filters?.dateTo) params.append('dateTo', filters.dateTo)
  if (filters?.limit) params.append('limit', filters.limit.toString())
  if (filters?.offset) params.append('offset', filters.offset.toString())

  const queryString = params.toString()
  const url = queryString ? `${API_ROUTES.REPORTS.BASE}?${queryString}` : API_ROUTES.REPORTS.BASE

  return get<ReportSearchResult>(url)
}

/**
 * Get a single report by ID
 */
export async function getReportById(id: string): Promise<Report> {
  const response = await get<ReportResponse>(API_ROUTES.REPORTS.BY_ID(id))
  return response.report
}

/**
 * Get reports for a specific study
 */
export async function getStudyReports(studyId: string): Promise<Report[]> {
  const response = await get<ReportsResponse>(`/studies/${studyId}/reports`)
  return response.reports
}

/**
 * Create a new report
 */
export async function createReport(data: CreateReportData): Promise<Report> {
  const response = await post<ReportResponse>(API_ROUTES.REPORTS.BASE, data)
  return response.report
}

/**
 * Update an existing report (only DRAFT/PRELIMINARY)
 */
export async function updateReport(id: string, data: UpdateReportData): Promise<Report> {
  const response = await post<ReportResponse>(API_ROUTES.REPORTS.UPDATE(id), data)
  return response.report
}

/**
 * Update report status
 */
export async function updateReportStatus(id: string, status: ReportStatus): Promise<Report> {
  const response = await post<ReportResponse>(
    API_ROUTES.REPORTS.STATUS(id),
    { status }
  )
  return response.report
}

/**
 * Sign a report (finalize from PRELIMINARY to FINAL)
 */
export async function signReport(id: string): Promise<Report> {
  const response = await post<ReportResponse>(API_ROUTES.REPORTS.SIGN(id), {})
  return response.report
}

/**
 * Amend a FINAL report
 */
export async function amendReport(id: string, data: AmendReportData): Promise<Report> {
  const response = await post<ReportResponse>(API_ROUTES.REPORTS.AMEND(id), data)
  return response.report
}

/**
 * Delete a report (only DRAFT, soft delete)
 */
export async function deleteReport(id: string): Promise<void> {
  await post(API_ROUTES.REPORTS.DELETE(id), {})
}

// =============================================================================
// Transcription Workflow
// =============================================================================

/**
 * Get transcription queue (DRAFT and TRANSCRIBING reports)
 * Used by transcriptors to see pending work
 */
export async function getTranscriptionQueue(): Promise<{ reports: Report[]; total: number }> {
  return get<{ reports: Report[]; total: number }>(API_ROUTES.REPORTS.TRANSCRIPTION_QUEUE)
}

/**
 * Get reports awaiting review (REVIEW status)
 * Used by radiologists to review transcribed reports
 */
export async function getAwaitingReview(): Promise<{ reports: Report[]; total: number }> {
  return get<{ reports: Report[]; total: number }>(API_ROUTES.REPORTS.AWAITING_REVIEW)
}

/**
 * Start transcription (DRAFT -> TRANSCRIBING)
 * Assigns the transcriptor to the report
 */
export async function startTranscription(id: string, audioUrl?: string): Promise<Report> {
  const response = await post<ReportResponse>(
    API_ROUTES.REPORTS.START_TRANSCRIPTION(id),
    { audioUrl }
  )
  return response.report
}

/**
 * Submit report for review (TRANSCRIBING -> REVIEW)
 * Used by transcriptors after completing transcription
 */
export async function submitForReview(id: string, transcriptionNotes?: string): Promise<Report> {
  const response = await post<ReportResponse>(
    API_ROUTES.REPORTS.SUBMIT_REVIEW(id),
    { transcriptionNotes }
  )
  return response.report
}

/**
 * Download report as PDF
 * Only available for FINAL or AMENDED reports
 */
export async function downloadReportPdf(id: string): Promise<Blob> {
  const response = await api.get(API_ROUTES.REPORTS.PDF(id), {
    responseType: 'blob',
  })
  return response.data as Blob
}

/**
 * Download report PDF and trigger browser download
 * Convenience function that handles the full download flow
 */
export async function downloadAndSaveReportPdf(
  id: string,
  filename?: string
): Promise<void> {
  const blob = await downloadReportPdf(id)

  // Create download link
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || `reporte_${id}.pdf`

  // Trigger download
  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// =============================================================================
// Signature Verification
// =============================================================================

/**
 * Verification result from the backend
 */
export interface VerificationResult {
  reportId: string
  reportStatus: string
  verification: {
    isValid: boolean
    signedAt: string | null
    signedBy: string | null
    signerEmail: string | null
    algorithm: string | null
    contentHashMatches: boolean
    errorMessage?: string
  }
  verifiedAt: string
}

/**
 * Verify report signature integrity
 * Only available for FINAL or AMENDED reports
 */
export async function verifyReportSignature(id: string): Promise<VerificationResult> {
  return get<VerificationResult>(API_ROUTES.REPORTS.VERIFY(id))
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format report date for display
 */
export function formatReportDate(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format report datetime for display
 */
export function formatReportDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Get status badge variant
 */
export function getReportStatusVariant(
  status: ReportStatus
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'DRAFT':
      return 'secondary'
    case 'TRANSCRIBING':
      return 'warning'
    case 'REVIEW':
      return 'warning'
    case 'PRELIMINARY':
      return 'warning'
    case 'FINAL':
      return 'success'
    case 'AMENDED':
      return 'default'
    case 'CANCELLED':
      return 'destructive'
    default:
      return 'default'
  }
}

// =============================================================================
// Addendum
// =============================================================================

/**
 * Una nota agregada a un informe ya firmado.
 *
 * No confundir con la enmienda: la enmienda reescribe el informe y emite una
 * version nueva que sustituye a la anterior. El addendum deja el original
 * intacto y añade debajo, con su fecha y su firma.
 */
export interface Addendum {
  id: string
  reportId: string
  content: string
  createdBy: string
  createdAt: string
  signatureHash: string
  firmante?: { nombre: string; email: string } | null
}

export async function agregarAddendum(
  reportId: string,
  contenido: string
): Promise<Addendum> {
  const respuesta = await post<{ addendum: Addendum }>(
    API_ROUTES.REPORTS.ADDENDUM(reportId),
    { contenido }
  )
  return respuesta.addendum
}

export async function getAddenda(reportId: string): Promise<Addendum[]> {
  const respuesta = await get<{ addenda: Addendum[] }>(API_ROUTES.REPORTS.ADDENDA(reportId))
  return respuesta.addenda
}
